import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import { toastError, toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import Breadcrumbs from '@Components/Breadcrumbs';
import Loader from '@Components/Loader';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useEvalRun, evalKeys } from '@Hooks/useEvals';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import cookies from 'browser-cookies';
import type { EvalResult, EvalStreamEvent, FailureCategory } from '@Types/eval';

const FAILURE_LABELS: Record<FailureCategory, string> = {
  tool_selection: 'Tool selection',
  tool_args: 'Tool args',
  hallucinated_data: 'Hallucination',
  over_refusal: 'Over-refusal',
  verbose: 'Verbose',
  wrong_action_payload: 'Wrong action',
  step_exhaustion: 'Step exhaustion',
  tool_error: 'Tool error',
  eval_error: 'Eval error',
  stale_memory: 'Stale memory',
  missing_memory: 'Missing memory',
  none: '—',
};

// True when a tool call's result contains a partial-failure signal.
const isToolErrorResult = (r: any): boolean => {
  if (!r || typeof r !== 'object' || r.simulated) return false;
  if (typeof r.error === 'string' && r.error.length > 0) return true;
  if (Array.isArray(r.errors) && r.errors.length > 0) return true;
  if (typeof r.failed === 'number' && r.failed > 0) return true;
  return false;
};

export default function RunDetails() {
  const { runId } = useParams<{ runId: string }>();
  const queryClient = useQueryClient();
  const [streamResults, setStreamResults] = useState<EvalResult[]>([]);
  const [progress, setProgress] = useState<{ completed: number; total: number } | null>(null);
  const [streamDone, setStreamDone] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isCancellingRef = useRef(false);

  // Initial fetch via TanStack Query — gives status and any existing results.
  const { data: run, status } = useEvalRun({
    runId,
    refetchInterval: streamDone ? false : 5000,
  });

  // Open NDJSON stream when run is RUNNING or PENDING. We track our own list
  // of streamed results so we can show them as they land without re-querying.
  useEffect(() => {
    if (!runId || !run) return;
    if (run.status !== 'RUNNING' && run.status !== 'PENDING') {
      setStreamDone(true);
      return;
    }
    if (abortRef.current) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const accessToken = cookies.get('at-admin') || cookies.get('at');
    const url = service.getEvalRunStreamUrl(runId, 0);

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `bearer ${accessToken}` },
          signal: ctrl.signal,
        });
        if (!res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const event = JSON.parse(line) as EvalStreamEvent;
              if (event.type === 'result') {
                setStreamResults((prev) => {
                  if (prev.some((r) => r.id === event.data.id)) return prev;
                  return [...prev, event.data];
                });
              } else if (event.type === 'progress') {
                setProgress({ completed: event.completed, total: event.total });
              } else if (event.type === 'done') {
                setStreamDone(true);
                queryClient.invalidateQueries({ queryKey: evalKeys.run(runId) });
              }
            } catch (e) {
              console.warn('Failed to parse stream line', line, e);
            }
          }
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.error('Stream error', e);
      }
    })();

    return () => {
      ctrl.abort();
      abortRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, run?.status]);

  const handleCancel = async () => {
    if (!runId) return;
    isCancellingRef.current = true;
    try {
      await service.cancelEvalRun(runId);
      toastSuccess('Cancellation requested.');
      await queryClient.invalidateQueries({ queryKey: evalKeys.run(runId) });
    } catch (e: any) {
      toastError(`Cancel failed: ${e?.message ?? e}`);
    } finally {
      isCancellingRef.current = false;
    }
  };

  const handleReEvaluate = async () => {
    if (!runId) return;
    try {
      await service.reEvaluateRun(runId);
      toastSuccess('Re-evaluated.');
      await queryClient.invalidateQueries({ queryKey: evalKeys.run(runId) });
    } catch (e: any) {
      toastError(`Re-evaluate failed: ${e?.message ?? e}`);
    }
  };

  if (status === 'pending') return <Loader size="lg" />;
  if (!run) return <div className="p-8 text-sm text-zinc-500">Run not found.</div>;

  // Merge persisted results with streamed results (de-dup by id)
  const allResults: EvalResult[] = [...(run.results ?? [])];
  for (const r of streamResults) {
    if (!allResults.some((x) => x.id === r.id)) allResults.push(r);
  }

  const totals: any = run.totals ?? {};
  const total = allResults.length;
  const passCount = allResults.filter((r) => r.passed).length;
  const failCount = total - passCount;
  const errorCount = allResults.filter((r) => r.errorMessage).length;
  const passRate = total > 0 ? Math.round((passCount / total) * 100) : 0;

  // Failure category histogram
  const categoryHist = new Map<FailureCategory, number>();
  for (const r of allResults) {
    if (r.passed) continue;
    const cat = (r.failureCategory ?? 'eval_error') as FailureCategory;
    categoryHist.set(cat, (categoryHist.get(cat) ?? 0) + 1);
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/evals' },
          { name: 'Evals', href: '/evals' },
          { name: run.suite?.name ?? 'Suite', href: `/evals/${run.suiteId}` },
          { name: 'Run', href: `/evals/runs/${run.id}` },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Run</Heading>
          <div className="mt-2 flex items-center gap-2">
            <Badge
              variant={
                run.status === 'COMPLETED' ? 'default'
                  : run.status === 'FAILED' ? 'destructive'
                  : 'outline'
              }
            >
              {run.status}
            </Badge>
            <span className="text-sm text-zinc-500">
              soul: {run.soul?.name ?? run.soulId}
            </span>
            {run.modelOverride && (
              <span className="text-sm text-zinc-500">model: {run.modelOverride}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {(run.status === 'RUNNING' || run.status === 'PENDING') && (
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          {streamDone && (
            <Button variant="outline" onClick={handleReEvaluate}>
              Re-evaluate
            </Button>
          )}
        </div>
      </div>

      {progress && !streamDone && (
        <Card className="mb-4">
          <CardContent className="py-4 text-sm">
            Progress: {progress.completed}/{progress.total}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-zinc-500">Pass rate</p>
            <p className="text-2xl font-semibold">{passRate}%</p>
            <p className="text-xs text-zinc-500">{passCount}/{total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-zinc-500">Failures</p>
            <p className="text-2xl font-semibold">{failCount}</p>
            <p className="text-xs text-zinc-500">{errorCount} errored</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-zinc-500">Total cost</p>
            <p className="text-2xl font-semibold">
              ${(totals.totalCostUsd ?? allResults.reduce((s, r) => s + r.costUsd, 0)).toFixed(4)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-zinc-500">Avg latency</p>
            <p className="text-2xl font-semibold">
              {total > 0
                ? Math.round(allResults.reduce((s, r) => s + r.latencyMs, 0) / total)
                : 0}ms
            </p>
          </CardContent>
        </Card>
      </div>

      {categoryHist.size > 0 && (
        <Card className="mb-6">
          <CardHeader><Heading level="h3">Failure categories</Heading></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {[...categoryHist.entries()].map(([cat, n]) => (
                <Badge key={cat} variant="outline">
                  {FAILURE_LABELS[cat]}: {n}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Drives prompt iteration — e.g. high "Tool args" → fix the system prompt's argument guidance.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><Heading level="h3">Per-case results</Heading></CardHeader>
        <CardContent>
          {allResults.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-500">Waiting for first result…</div>
          ) : (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Case</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Latency</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Failure</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allResults.map((r) => (
                  <>
                    <TableRow
                      key={r.id}
                      onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                      className="cursor-pointer"
                    >
                      <TableCell>{r.case?.name ?? r.caseId}</TableCell>
                      <TableCell>
                        <Badge variant={r.passed ? 'default' : 'destructive'}>
                          {r.passed ? 'PASS' : r.errorMessage ? 'ERROR' : 'FAIL'}
                        </Badge>
                      </TableCell>
                      <TableCell>{(r.score ?? 0).toFixed(2)}</TableCell>
                      <TableCell>{r.stepsUsed}</TableCell>
                      <TableCell>{r.latencyMs}ms</TableCell>
                      <TableCell>${r.costUsd.toFixed(4)}</TableCell>
                      <TableCell>
                        {r.failureCategory && r.failureCategory !== 'none' ? (
                          <Badge variant="outline">{FAILURE_LABELS[r.failureCategory] ?? r.failureCategory}</Badge>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {expandedId === r.id && (
                      <TableRow key={`${r.id}-expand`}>
                        <TableCell colSpan={7} className="bg-zinc-50 dark:bg-zinc-900">
                          <ResultExpanded result={r} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MemorySnapshotBlock({ snapshot }: { snapshot: any }) {
  const bos: any[] = snapshot?.businessObjects ?? [];
  const refs: any[] = snapshot?.toolMemoryRefs ?? [];
  if (bos.length === 0 && refs.length === 0) return null;
  return (
    <details open>
      <summary className="text-xs font-medium cursor-pointer">
        Memory snapshot — {bos.length} BO{bos.length === 1 ? '' : 's'}, {refs.length} tool-memory ref{refs.length === 1 ? '' : 's'}
      </summary>
      <div className="mt-2 space-y-1 text-xs">
        {bos.length > 0 && (
          <div>
            <p className="font-semibold text-zinc-500">Business objects</p>
            <ul className="ml-4">
              {bos.map((b) => (
                <li key={b.key}>
                  <code className="text-[11px]">{b.key}</code>
                  {' '}<span className="text-zinc-500">({b.entityType}, count={b.count})</span>
                  {b.summary ? <span className="text-zinc-400"> — {b.summary}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        {refs.length > 0 && (
          <div>
            <p className="font-semibold text-zinc-500">Tool memory refs</p>
            <ul className="ml-4">
              {refs.map((r) => (
                <li key={r.refId}>
                  <code className="text-[11px]">{r.refId}</code>
                  {' '}<span className="text-zinc-500">from {r.toolName}</span>
                  {r.summary ? <span className="text-zinc-400"> — {r.summary}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}

function ToolCallsBlock({ toolCalls, simulatedCount }: { toolCalls: any[]; simulatedCount?: number }) {
  const errorCount = (toolCalls ?? []).filter((tc) => isToolErrorResult(tc?.result ?? tc?.output)).length;
  return (
    <details>
      <summary className="text-xs font-medium cursor-pointer">
        Tool calls ({toolCalls?.length ?? 0})
        {simulatedCount ? (
          <Badge variant="outline" className="ml-2">{simulatedCount} simulated</Badge>
        ) : null}
        {errorCount > 0 ? (
          <Badge variant="destructive" className="ml-2">{errorCount} tool error{errorCount === 1 ? '' : 's'}</Badge>
        ) : null}
      </summary>
      <div className="mt-2 space-y-2">
        {(toolCalls ?? []).map((tc, i) => {
          const result = tc?.result ?? tc?.output;
          const errored = isToolErrorResult(result);
          return (
            <div
              key={i}
              className={
                'rounded border p-2 ' +
                (errored
                  ? 'border-red-300 dark:border-red-800 bg-red-50/30 dark:bg-red-950/10'
                  : 'border-zinc-200 dark:border-zinc-800')
              }
            >
              <pre className="text-[11px] whitespace-pre-wrap">{JSON.stringify(tc, null, 2)}</pre>
            </div>
          );
        })}
      </div>
    </details>
  );
}

function ResultExpanded({ result }: { result: EvalResult }) {
  const trace = (result.messageTrace ?? {}) as any;
  const isChain = trace.kind === 'chain' && Array.isArray(trace.steps);

  if (result.errorMessage) {
    return (
      <div className="space-y-3 py-3">
        <div className="text-xs text-red-600">{result.errorMessage}</div>
      </div>
    );
  }

  if (isChain) {
    const steps: any[] = trace.steps;
    return (
      <div className="space-y-3 py-3">
        <p className="text-xs text-zinc-500">
          Chain trajectory — {steps.length} step{steps.length === 1 ? '' : 's'}.
          Cost ${result.costUsd.toFixed(4)} / {result.latencyMs}ms total /
          {' '}{result.stepsUsed} agent tool-call steps across all turns.
          {result.toolErrorCount > 0 && (
            <Badge variant="destructive" className="ml-2">{result.toolErrorCount} tool error{result.toolErrorCount === 1 ? '' : 's'}</Badge>
          )}
        </p>
        {steps.map((s, i) => (
          <div
            key={i}
            className={
              'rounded-md border p-3 ' +
              (s.passed
                ? 'border-zinc-200 dark:border-zinc-800'
                : 'border-red-300 dark:border-red-800 bg-red-50/30 dark:bg-red-950/10')
            }
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline">Step {i + 1}</Badge>
                <Badge variant={s.passed ? 'default' : 'destructive'}>
                  {s.passed ? 'PASS' : 'FAIL'}
                </Badge>
                {s.failureCategory && s.failureCategory !== 'none' && (
                  <Badge variant="outline">
                    {FAILURE_LABELS[s.failureCategory as FailureCategory] ?? s.failureCategory}
                  </Badge>
                )}
                {s.simulatedActions?.length ? (
                  <Badge variant="outline">{s.simulatedActions.length} simulated</Badge>
                ) : null}
                {s.toolErrorCount > 0 ? (
                  <Badge variant="destructive">{s.toolErrorCount} tool error{s.toolErrorCount === 1 ? '' : 's'}</Badge>
                ) : null}
              </div>
              <div className="text-xs text-zinc-500">
                {s.latencyMs}ms · ${(s.costUsd ?? 0).toFixed(4)} · {s.stepsUsed} tool steps · score {(s.score ?? 0).toFixed(2)}
              </div>
            </div>
            <div className="text-xs mb-2">
              <span className="font-semibold text-zinc-500">User:</span>{' '}
              <span className="whitespace-pre-wrap">{s.userMessage}</span>
            </div>
            <details>
              <summary className="text-xs font-medium cursor-pointer">Assistant response</summary>
              <pre className="text-xs whitespace-pre-wrap p-2 bg-white dark:bg-zinc-950 rounded mt-2">
                {s.finalText || '(none)'}
              </pre>
            </details>
            <MemorySnapshotBlock snapshot={s.memorySnapshot} />
            <ToolCallsBlock toolCalls={s.toolCalls ?? []} simulatedCount={(s.simulatedActions ?? []).length} />
            <details>
              <summary className="text-xs font-medium cursor-pointer">Evaluator outputs</summary>
              <pre className="text-xs whitespace-pre-wrap p-2 bg-white dark:bg-zinc-950 rounded mt-2">
                {JSON.stringify(s.evaluators, null, 2)}
              </pre>
            </details>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3 py-3">
      {result.toolErrorCount > 0 && (
        <div className="text-xs">
          <Badge variant="destructive">{result.toolErrorCount} tool error{result.toolErrorCount === 1 ? '' : 's'}</Badge>
        </div>
      )}
      <details>
        <summary className="text-sm font-medium cursor-pointer">Final text</summary>
        <pre className="text-xs whitespace-pre-wrap p-2 bg-white dark:bg-zinc-950 rounded mt-2">
          {result.finalText || '(none)'}
        </pre>
      </details>
      <MemorySnapshotBlock snapshot={result.memorySnapshot} />
      <ToolCallsBlock
        toolCalls={result.toolCalls ?? []}
        simulatedCount={(result.simulatedActions ?? []).length}
      />
      <details>
        <summary className="text-sm font-medium cursor-pointer">Evaluator outputs</summary>
        <pre className="text-xs whitespace-pre-wrap p-2 bg-white dark:bg-zinc-950 rounded mt-2">
          {JSON.stringify(result.evaluators, null, 2)}
        </pre>
      </details>
    </div>
  );
}
